import React, { memo, useCallback, useState } from 'react'
import styled from 'styled-components'
import { GroupProps } from '.'
import { GroupHeader } from '../GroupHeader'
import { MediaResultFromServer } from '../../App'
import { Translate, useTranslations } from '../../TranslationsContext'
import { MediaDetailOverlay } from '../MediaDetailOverlay'

export const MediaGrid: React.FC<GroupProps> = memo(
  ({ group, totalGroups, isInlineLoading, onSeeMore, fetchNextPage, apiHost, hideFilter, children }) => {
    const { outOf, result, results } = useTranslations()

    const [focusedAsset, _setFocusedAsset] = useState<string | undefined>(
      new URLSearchParams(window.location.search).get('mediaId') ?? undefined
    )
    const setFocusedAsset = useCallback((asset: string | undefined) => {
      new URLSearchParams(window.location.search).get('mediaId')
      const params = new URLSearchParams(window.location.search)
      asset === undefined ? params.delete('mediaId') : params.set('mediaId', asset)
      const hash = window.location.hash.split('?')[0]
      window.history.replaceState(undefined, '', `${hash}?${params.toString()}`)
      _setFocusedAsset(asset)
    }, [])

    const onCloseOverlay = useCallback(() => setFocusedAsset(undefined), [setFocusedAsset])

    return (
      <>
        <div key={group.title} style={{ paddingBottom: '24px' }}>
          {totalGroups > 1 ? (
            <GroupHeader
              title={group.title}
              pagination={`${group.results.length} ${outOf} ${group.total} ${group.total === 0 ? result : results}`}
              onSeeMore={group.total <= group.results.length ? undefined : () => onSeeMore(group.filter.value)}
            />
          ) : children !== false ? (
            <div>&nbsp;</div>
          ) : (
            <></>
          )}

          <div className="grid-row">
            {((group.results as unknown) as ReadonlyArray<MediaResultFromServer>).map((media, i) => {
              return <Result key={i} hideFilter={hideFilter} onClick={() => setFocusedAsset(media.id)} {...media} />
            })}
          </div>

          {isInlineLoading && (
            <div>
              <h3 className="grid-text-center dots grid-gap3-above grid-gap3-below">
                <Translate id="loading" />
                <span>.</span>
                <span>.</span>
                <span>.</span>
                <p></p>
              </h3>
            </div>
          )}

          {totalGroups === 1 && group.total > group.results.length && (
            <div className="grid-text-center grid-gap2-above">
              <button className="button" onClick={() => fetchNextPage()}>
                <Translate id="loadMoreResults" />
              </button>
            </div>
          )}
        </div>

        {focusedAsset !== undefined && (
          <MediaDetailOverlay mediaId={focusedAsset} onClose={onCloseOverlay} apiHost={apiHost} />
        )}
      </>
    )
  }
)

const Img = styled.div<{ src: string }>`
  height: 150px;
  width: 100%;

  background-image: url('${(p) => p.src}');
  background-size: contain;
  background-position: 50% 50%;
  background-repeat: no-repeat;

  margin-bottom: 8px;
`

const PreTitle = styled.p`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 19px;
  text-align: center;
  color: #757575;
  text-transform: uppercase;
  margin-bottom: 4px;
`

const Title = styled.p`
  font-family: Roboto Condensed;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 20px;

  display: flex;
  align-items: center;
  text-align: center;
  color: #303030;

  min-height: 40px;
  margin-bottom: 0;
  text-align: center;
  align-items: center;
  justify-content: center;
  white-space: break-all;
  word-break: break-all;

  transition: all 200ms;
`

const MediaWrapper = styled.div`
  position: relative;
  padding: 8px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: white;
    box-shadow: 0px 0px 25px #e7e7e7;

    ${Title} {
      color: #56a0d3;
      text-decoration: underline;
    }
  }

  transition: all 200ms;
`

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)).toString() + ' ' + sizes[i]
}

const Result: React.FC<MediaResultFromServer & { onClick: () => void; hideFilter: boolean }> = memo(
  ({ onClick, hideFilter, ...media }) => {
    return (
      <MediaWrapper
        onClick={onClick}
        className={
          hideFilter
            ? 'grid-span3 grid-omega4 grid-gap2-below grid-tablet-span6 grid-tablet-omega2 grid-mobile-span12 grid-mobile-omega1'
            : 'grid-span4 grid-omega3 grid-gap2-below grid-tablet-span6 grid-tablet-omega2 grid-mobile-span12 grid-mobile-omega1'
        }
      >
        <Img src={media.thumbnail} />
        <PreTitle>
          {media.extension.map((e) => e.toUpperCase()).join(' ')} {formatBytes(media.fileSize)}
        </PreTitle>
        <Title>{media.title}</Title>
      </MediaWrapper>
    )
  }
)
